import useVehicleChargeSchedulesFetcher from '@/authenticated/hooks/useVehicleChargeSchedulesFetcher'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { utilityProgramEnrollmentCollection } from '@/reducers/utilityProgramEnrollments'
import {
  selectUserCanAndHasntIntegratedUtility,
  selectUserVehicleIsEnrolledProgramEligible,
} from '@/selectors'

import {
  selectUtilityProgramForUserUtility,
  utilityProgramCollection,
} from '@/reducers/utilityPrograms'
import { Vehicle, VehicleChargeSchedule } from '@/types'
import EnvironmentalChargingOptionsCard from '@/authenticated/components/ChargeSchedule/EnvironmentalChargingOptionsCard'
import { useCallback, useEffect, useRef, useState } from 'react'
import debounce from 'lodash.debounce'
import { updateChargeSchedule } from '@/actions/schedule'
import { ButtonVariant, Divider } from '@/components'
import { localGridDetails } from '../../schedule/vehicle/local-grid-support-details/localGridDetails'
import * as Sentry from '@sentry/browser'
import { vehicleEnrollmentCollection } from '@/reducers/deviceProgramEnrollments'
import { canCharge } from '@/utils/vehicles/permissions'
import {
  programsNewCollection,
  selectAvailablePrograms,
  selectEnrolledPrograms,
} from '@/reducers/programsNew'
import useDeviceEnrollments from '@/hooks/useDeviceEnrollments/useDeviceEnrollments'
import { ID } from '@/types/model'
import FeedbackMessageSurveyWithContactForm from '@/components/FeedbackMessageSurveyWithContactForm'

type Props = {
  vehicle: Vehicle
  buttonVariant?: ButtonVariant
  showUtilityUpsell?: boolean
}

function useCanControlVehicleEnrollment(vehicle: Vehicle) {
  const vehicleIsEligibleForProfileEnrolledProgram = useAppSelector((state) =>
    selectUserVehicleIsEnrolledProgramEligible(state, vehicle.id),
  )
  const vehicleHasEnrolledInProgram = useAppSelector((state) =>
    Boolean(
      vehicleEnrollmentCollection.selectors
        .selectAll(state)
        .find((enrollment) => enrollment.vehicle_id === vehicle.id),
    ),
  )

  return (
    vehicleIsEligibleForProfileEnrolledProgram || vehicleHasEnrolledInProgram
  )
}

export function useViewModel({
  vehicle,
  buttonVariant,
  showUtilityUpsell = true,
}: Props) {
  const dispatch = useAppDispatch()
  const chargeSchedules = useVehicleChargeSchedulesFetcher()
  const { isLoadingOrIdle: vehicleEnrollmentIsLoadingOrIdle } =
    vehicleEnrollmentCollection.useFetch()
  const { isLoadingOrIdle: utilityProgramEnrollmentIsLoadingOrIdle } =
    utilityProgramEnrollmentCollection.useFetch()
  const { isLoadingOrIdle: utilityProgramIsLoadingOrIdle } =
    utilityProgramCollection.useFetch()
  const { isLoadingOrIdle: programsNewCollectionIsLoadingOrIdle } =
    programsNewCollection.useFetch()
  const hasEnrolledMultiPrograms =
    useAppSelector(selectEnrolledPrograms(true)).length > 0
  const hasAvailableMultiPrograms =
    useAppSelector(selectAvailablePrograms(true))?.length > 0
  const hasMultiPrograms = hasEnrolledMultiPrograms || hasAvailableMultiPrograms

  const { deviceEnrollments, isLoading: fetchingEnrolledPrograms } =
    useDeviceEnrollments('vehicle', vehicle.id)

  const hasEnrolledPrograms =
    deviceEnrollments
      .map((deviceEnrollment) => deviceEnrollment.enrollment)
      .filter(Boolean)?.length > 0

  const cachedHasEnrolledPrograms = useRef<boolean>(false)

  if (hasEnrolledPrograms) {
    cachedHasEnrolledPrograms.current = hasEnrolledPrograms
  }

  useEffect(() => {
    Sentry.addBreadcrumb({ data: { chargeSchedules } })
  }, [chargeSchedules])
  const chargeSchedule = (chargeSchedules ?? []).find(
    (schedule) => schedule.vehicle_id === vehicle.id,
  ) as VehicleChargeSchedule | undefined

  const canVehicleCharge = canCharge(vehicle)
  const [localChargeSchedule, setLocalChargeSchedule] = useState(chargeSchedule)
  const AllEligibleProgramsAreLocalGridPrograms = useAppSelector((state) => {
    const localGridNames = localGridDetails.map((program) => program.name)
    return utilityProgramEnrollmentCollection.selectors
      .selectAll(state)
      .every((enrollment) =>
        localGridNames.includes(enrollment.utility_program.name),
      )
  })

  const canControlVehicleEnrollment = useCanControlVehicleEnrollment(vehicle)
  const userHasUnenrolledProgram = useAppSelector((state) =>
    selectUtilityProgramForUserUtility(state),
  )
  const userCanAndHasntConnectedUtility = useAppSelector(
    selectUserCanAndHasntIntegratedUtility,
  )

  const debouncedSave = useCallback(
    debounce((model) => {
      dispatch(updateChargeSchedule(model))
    }, 500),
    [],
  )

  const onChargeScheduleChange = (chargeSchedule: VehicleChargeSchedule) => {
    setLocalChargeSchedule(chargeSchedule)
    debouncedSave(chargeSchedule)
  }

  // Ensure the local charge schedule is in sync with what we pull from the server.
  // This fixed a bug where local charge schedule was not being initialized properly
  // when the component was first mounted.
  useEffect(() => {
    if (localChargeSchedule?.id !== chargeSchedule?.id && chargeSchedule) {
      setLocalChargeSchedule(chargeSchedule)
    }
  }, [chargeSchedules, localChargeSchedule])

  // if vehicle is enrolled in a program, show toggle header
  // if vehicle is not enrolled in a program, but is eligible for a profile enrolled program, show toggle header
  // if vehicle is not enrolled in a program, and is not eligible for a profile enrolled program, don't show toggle header
  const shouldShowProgramHeader =
    !AllEligibleProgramsAreLocalGridPrograms && canControlVehicleEnrollment

  const [unenrollSurveyOpen, setUnenrollSurveyOpen] = useState(false)
  const openSurvey = () => setUnenrollSurveyOpen(true)
  const closeSurvey = () => setUnenrollSurveyOpen(false)

  return {
    vehicle,
    buttonVariant,
    showUtilityUpsell,
    shouldShowProgramHeader,
    canVehicleCharge,
    localChargeSchedule,
    onChargeScheduleChange,
    userHasUnenrolledProgram,
    userCanAndHasntConnectedUtility,
    isLoading:
      vehicleEnrollmentIsLoadingOrIdle ||
      utilityProgramEnrollmentIsLoadingOrIdle ||
      utilityProgramIsLoadingOrIdle ||
      programsNewCollectionIsLoadingOrIdle ||
      (fetchingEnrolledPrograms && !cachedHasEnrolledPrograms.current),
    hasMultiPrograms,
    unenrollSurveyOpen,
    openSurvey,
    closeSurvey,
  }
}

const HeaderSection = ({
  vehicleId,
  shouldShowProgramHeader,
  canVehicleCharge,
  onUnenroll,
}: {
  vehicleId: ID
  shouldShowProgramHeader: boolean
  canVehicleCharge: boolean
  onUnenroll: () => void
}) => {
  if (shouldShowProgramHeader) {
    return (
      <>
        <EnvironmentalChargingOptionsCard.EnrollmentToggleHeader
          vehicleId={vehicleId}
          onUnenroll={onUnenroll}
        />
        {canVehicleCharge && <Divider />}
      </>
    )
  }

  return <EnvironmentalChargingOptionsCard.PlainHeader />
}

const AirQualitySection = ({
  canVehicleCharge,
  shouldShowProgramHeader,
  chargeSchedule,
  onChange,
}: {
  canVehicleCharge: boolean
  shouldShowProgramHeader: boolean
  chargeSchedule: VehicleChargeSchedule | undefined
  onChange: (s: VehicleChargeSchedule) => void
}) => {
  if (!canVehicleCharge) return null

  return (
    <>
      <EnvironmentalChargingOptionsCard.AirQualityToggle
        chargeSchedule={chargeSchedule}
        onChange={onChange}
      />
      {!shouldShowProgramHeader && <Divider />}
    </>
  )
}

const ProgramSection = ({
  shouldShowProgramHeader,
  userHasUnenrolledProgram,
  userCanAndHasntConnectedUtility,
  showUtilityUpsell,
  chargeSchedule,
  onChange,
  vehicleId,
  buttonVariant,
}: {
  shouldShowProgramHeader: boolean
  userHasUnenrolledProgram: boolean
  userCanAndHasntConnectedUtility: boolean
  showUtilityUpsell: boolean
  chargeSchedule: VehicleChargeSchedule | undefined
  onChange: (s: VehicleChargeSchedule) => void
  vehicleId: ID
  buttonVariant?: ButtonVariant
}) => {
  if (shouldShowProgramHeader) return null

  if (userHasUnenrolledProgram) {
    return (
      <EnvironmentalChargingOptionsCard.ProgramEnrollmentUpsell
        buttonVariant={buttonVariant}
        vehicleId={vehicleId}
      />
    )
  }

  if (userCanAndHasntConnectedUtility && showUtilityUpsell) {
    return (
      <EnvironmentalChargingOptionsCard.UtilityIntegrationUpsell
        chargeSchedule={chargeSchedule}
        onChange={onChange}
      />
    )
  }

  return (
    <EnvironmentalChargingOptionsCard.GridSuportToggle
      chargeSchedule={chargeSchedule}
      onChange={onChange}
    />
  )
}

export default function VehicleProgramCard(props: Props) {
  const {
    vehicle,
    buttonVariant,
    showUtilityUpsell,
    shouldShowProgramHeader,
    canVehicleCharge,
    localChargeSchedule,
    onChargeScheduleChange,
    userHasUnenrolledProgram,
    userCanAndHasntConnectedUtility,
    hasMultiPrograms,
    unenrollSurveyOpen,
    openSurvey,
    closeSurvey,
  } = useViewModel(props)

  const loadedContent = (
    <EnvironmentalChargingOptionsCard>
      <HeaderSection
        vehicleId={vehicle.id}
        shouldShowProgramHeader={shouldShowProgramHeader && !hasMultiPrograms}
        canVehicleCharge={canVehicleCharge}
        onUnenroll={openSurvey}
      />

      <AirQualitySection
        canVehicleCharge={canVehicleCharge}
        shouldShowProgramHeader={shouldShowProgramHeader}
        chargeSchedule={localChargeSchedule}
        onChange={onChargeScheduleChange}
      />

      <ProgramSection
        shouldShowProgramHeader={shouldShowProgramHeader && !hasMultiPrograms}
        userHasUnenrolledProgram={!!userHasUnenrolledProgram}
        userCanAndHasntConnectedUtility={userCanAndHasntConnectedUtility}
        showUtilityUpsell={(showUtilityUpsell ?? true) && !hasMultiPrograms}
        chargeSchedule={localChargeSchedule}
        onChange={onChargeScheduleChange}
        vehicleId={vehicle.id}
        buttonVariant={buttonVariant}
      />
    </EnvironmentalChargingOptionsCard>
  )

  return (
    <>
      {loadedContent}
      <FeedbackMessageSurveyWithContactForm
        onClose={closeSurvey}
        open={unenrollSurveyOpen}
        onSubmit={closeSurvey}
        surveyType={'ProgramUnenrollment'}
        supportTitle="Feedback: User Program Unenrollment"
      />
    </>
  )
}
